import React, { useEffect, useRef } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { useScrollLock } from '@/hooks/useScrollLock';
import { observeStableVisualViewport } from '@/utils/stableVisualViewport';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
}

const AuthModal: React.FC<AuthModalProps> = ({ isOpen, onClose, children }) => {
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const dialogRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  useScrollLock(isOpen);

  // iOS Safari keeps fixed elements sized to the layout viewport while the
  // keyboard shrinks only the visual viewport, so a full-height overlay has
  // nothing to scroll and the fields end up under the keyboard. Pin the
  // overlay's top to the visible area and give it a keyboard-sized spacer so
  // it overflows and can scroll the focused field above the keyboard. The
  // overlay itself still reaches the bottom of the screen, so the card keeps
  // rendering behind the translucent keyboard instead of being clipped.
  useEffect(() => {
    if (!isOpen) return;
    const dialog = dialogRef.current;
    const overlay = overlayRef.current;
    if (!dialog || !overlay) return;

    return observeStableVisualViewport(({ top, height }) => {
      const keyboardInset = Math.max(0, dialog.clientHeight - height);
      overlay.style.top = `${top}px`;
      overlay.style.height = `${height + keyboardInset}px`;
      overlay.style.setProperty('--keyboard-inset', `${keyboardInset}px`);

      const active = document.activeElement;
      if (active instanceof HTMLElement && overlay.contains(active)) {
        // Centre within the visible area (not the whole overlay, which runs
        // under the keyboard) so iOS's floating autofill bar, which sits just
        // above the keyboard, doesn't cover the field's edge.
        const field = active.getBoundingClientRect();
        const visibleTop = overlay.getBoundingClientRect().top;
        overlay.scrollTop += field.top + field.height / 2 - (visibleTop + height / 2);
      }
    });
  }, [isOpen]);

  // Firefox on iOS ends fixed elements (this modal, its backdrop and the GSAP
  // ScrollSmoother page wrapper) above its address bar while the keyboard is
  // open, so the root background shows through the translucent bar and
  // keyboard instead. Dim it to match the backdrop.
  useEffect(() => {
    if (!isOpen) return;
    const root = document.documentElement;
    const previous = root.style.backgroundColor;
    const base = getComputedStyle(root).backgroundColor;
    root.style.backgroundColor = `color-mix(in srgb, ${base} 50%, black)`;
    return () => {
      root.style.backgroundColor = previous;
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape =(event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      ref={dialogRef}
      className="fixed inset-0 z-50"
      aria-labelledby="auth-modal-title"
      role="dialog"
      aria-modal="true"
    >
      {/* The backdrop stays full-screen, separate from the scroll container,
          because the iOS keyboard is translucent and would otherwise show the
          undimmed page through it. It extends below the layout viewport to
          cover the keyboard area when Safari pans the visual viewport down. */}
      <div className="fixed inset-x-0 top-0 -bottom-[100lvh] bg-black/50" aria-hidden="true" />
      <div ref={overlayRef} className="fixed inset-0 overflow-y-auto overscroll-contain">
      {/* Centring wrapper that also scrolls when the card is taller than a short
          (e.g. landscape) viewport, so no fields end up off-screen. Its minimum
          height excludes the keyboard so the card centres in the visible area. */}
      <div className="flex min-h-[calc(100%_-_var(--keyboard-inset,0px))] items-center justify-center p-4 2xs:p-8 pt-[max(1rem,var(--safe-top))] pb-[max(1rem,var(--safe-bottom))]">
      <div
        ref={modalRef}
        className={`relative rounded-lg shadow-custom w-full max-w-lg ${
          isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white'
        }`}
      >
        <div className="relative">
          <button
            onClick={onClose}
            className={`absolute top-4 right-4 p-2 rounded-full cursor-pointer transition-colors ${
              isDarkMode
                ? 'text-gray-400 hover:bg-gray-700 hover:text-white'
                : 'text-gray-500 hover:bg-gray-200 hover:text-gray-900'
            }`}
            aria-label="Close modal"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              ></path>
            </svg>
          </button>
          <div className="p-8">{children}</div>
        </div>
      </div>
      </div>
      <div className="h-[var(--keyboard-inset,0px)]" aria-hidden="true" />
      </div>
    </div>
  );
};

export default AuthModal;